/**
 * Consolidated Aleo record utilities.
 *
 * Merges three previously separate modules:
 *   - src/utils/aleoRecordParser.ts  — generic plaintext parsing
 *   - src/utils/shieldRecord.ts      — Shield wallet types + parsers
 *   - src/utils/bridge/aleoWalletRecord.ts — bridge record utilities
 *
 * All original module paths re-export from here for backwards compatibility.
 */

import { IPuzzleAleoRecord, RecordStatus } from '@/interfaces/bridge/record.interface';
import { leo2js } from '@/lib/aleo';

// ---------------------------------------------------------------------------
// Generic plaintext parsing (from aleoRecordParser.ts)
// ---------------------------------------------------------------------------

export interface RecordPlainText {
  owner: string;
  _nonce: string;
  _version: string;
  [key: string]: string;
}

export interface SaltRecordPlaintext extends RecordPlainText {
  salt: string;
}

export interface TokenRecordPlainText extends RecordPlainText {
  token_id: string;
  amount: string;
  external_authorization_required: string;
  authorized_until: string;
}

export function parseAleoObject(str: string): RecordPlainText | null {
  try {
    if (str.startsWith('"') && str.endsWith('"')) {
      str = str.slice(1, -1);
    }
    let cleaned = str.replace(/\.private/g, '').replace(/\.public/g, '');
    cleaned = cleaned.replace(/u\d+|field|group/g, '');
    cleaned = cleaned.replace(/(\w+):/g, '"$1":');
    cleaned = cleaned.replace(/:\s*([a-zA-Z0-9]+)/g, ':"$1"');
    cleaned = cleaned.replace(/,\s*}/g, '}');
    const obj = JSON.parse(cleaned);
    return obj;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Shield wallet types (from shieldRecord.ts)
// ---------------------------------------------------------------------------

/**
 * Represents the plaintext data of an Aleo Token record
 */
export interface TokenRecordPlaintext {
  owner: string;
  amount: string; // u128
  token_id: string; // field
  external_authorization_required: boolean;
  authorized_until: string; // u32 timestamp
  _nonce: string; // group
  _version: string; // u8
}

/**
 * Represents a complete Aleo on-chain record from token_registry.aleo
 */
export interface ShieldRecord {
  blockHeight: number;
  blockTimestamp: number;
  commitment: string; // field
  functionName: string;
  outputIndex: number;
  owner: string; // field
  programName: string;
  recordCiphertext: string;
  recordName: string;
  sender: string; // Aleo address (aleo1...)
  spent: boolean;
  tag: string; // field
  transactionId: string;
  transitionId: string;
  transactionIndex: number;
  transitionIndex: number;
  recordPlaintext: string | TokenRecordPlaintext;
}

export function isTokenRecordPlaintext(
  plaintext: string | TokenRecordPlaintext,
): plaintext is TokenRecordPlaintext {
  return typeof plaintext === 'object';
}

export function parseTokenRecordPlaintext(plaintextStr: string): TokenRecordPlaintext {
  const getValue = (key: string) => {
    const match = plaintextStr.match(new RegExp(`${key}:\\s*([^,}\\n]+)`));
    return match?.[1]?.trim().replace(/\.private|\.public/g, '') ?? '';
  };

  return {
    owner: getValue('owner'),
    amount: getValue('amount'),
    token_id: getValue('token_id'),
    external_authorization_required: getValue('external_authorization_required') === 'true',
    authorized_until: getValue('authorized_until'),
    _nonce: getValue('_nonce'),
    _version: getValue('_version'),
  };
}

// ---------------------------------------------------------------------------
// Bridge record utilities (from aleoWalletRecord.ts)
// ---------------------------------------------------------------------------

export const BRIDGE_ALEO_WALLET = {
  PUZZLE: 'Puzzle Wallet',
  SHIELD: 'Shield Wallet',
} as const;

export type BridgeAleoWalletType = (typeof BRIDGE_ALEO_WALLET)[keyof typeof BRIDGE_ALEO_WALLET];
export type BridgeAleoRecord = IPuzzleAleoRecord | ShieldRecord;

export type PuzzleDecryptedRecordsResult = {
  walletType: typeof BRIDGE_ALEO_WALLET.PUZZLE;
  filteredRecords: IPuzzleAleoRecord[];
  bestRecord: IPuzzleAleoRecord | null;
};

export type ShieldDecryptedRecordsResult = {
  walletType: typeof BRIDGE_ALEO_WALLET.SHIELD;
  filteredRecords: ShieldRecord[];
  bestRecord: ShieldRecord | null;
};

export type UnknownDecryptedRecordsResult = {
  walletType: null;
  filteredRecords: [];
  bestRecord: null;
};

export type BridgeDecryptedRecordsResult =
  | PuzzleDecryptedRecordsResult
  | ShieldDecryptedRecordsResult
  | UnknownDecryptedRecordsResult;

const ensureLeoValueSuffix = (value: string, suffix: 'field' | 'u128') => {
  return value.endsWith(suffix) ? value : `${value}${suffix}`;
};

export const getBridgeAleoWalletType = (walletName?: string | null): BridgeAleoWalletType | null => {
  if (!walletName) return null;
  if (walletName.includes(BRIDGE_ALEO_WALLET.PUZZLE)) return BRIDGE_ALEO_WALLET.PUZZLE;
  if (walletName.includes(BRIDGE_ALEO_WALLET.SHIELD)) return BRIDGE_ALEO_WALLET.SHIELD;
  return null;
};

export const isPuzzleAleoRecord = (record: BridgeAleoRecord): record is IPuzzleAleoRecord => {
  return 'data' in record && 'status' in record;
};

export const isShieldAleoRecord = (record: BridgeAleoRecord): record is ShieldRecord => {
  return 'recordPlaintext' in record && 'spent' in record;
};

export const getShieldTokenRecordPlaintext = (
  record: ShieldRecord,
): TokenRecordPlaintext | null => {
  if (isTokenRecordPlaintext(record.recordPlaintext)) {
    return record.recordPlaintext;
  }
  try {
    return parseTokenRecordPlaintext(record.recordPlaintext);
  } catch {
    return null;
  }
};

export const getBridgeRecordTokenId = (record: BridgeAleoRecord): bigint | null => {
  if (isPuzzleAleoRecord(record)) {
    return leo2js.field(ensureLeoValueSuffix(record.data.token_id, 'field'));
  }
  const plaintext = getShieldTokenRecordPlaintext(record);
  if (!plaintext) return null;
  return leo2js.field(ensureLeoValueSuffix(plaintext.token_id, 'field'));
};

export const getBridgeRecordAmount = (record: BridgeAleoRecord): bigint => {
  if (isPuzzleAleoRecord(record)) {
    return leo2js.u128(ensureLeoValueSuffix(record.data.amount, 'u128'));
  }
  const plaintext = getShieldTokenRecordPlaintext(record);
  if (!plaintext) return 0n;
  return leo2js.u128(ensureLeoValueSuffix(plaintext.amount, 'u128'));
};

export const isBridgeRecordUnspent = (record: BridgeAleoRecord): boolean => {
  if (isPuzzleAleoRecord(record)) {
    return record.status === RecordStatus.Unspent;
  }
  return !record.spent;
};

export const isBridgeTokenRecordMatch = (
  record: BridgeAleoRecord,
  amount: bigint,
  tokenId: bigint,
): boolean => {
  const recordTokenId = getBridgeRecordTokenId(record);
  if (recordTokenId === null) return false;
  return (
    recordTokenId === tokenId &&
    getBridgeRecordAmount(record) >= amount &&
    isBridgeRecordUnspent(record)
  );
};

export const filterPuzzleTokenRecords = (
  records: IPuzzleAleoRecord[],
  amount: bigint,
  tokenId: bigint,
): PuzzleDecryptedRecordsResult => {
  const filteredRecords = records.filter((record) =>
    isBridgeTokenRecordMatch(record, amount, tokenId),
  );
  return {
    walletType: BRIDGE_ALEO_WALLET.PUZZLE,
    filteredRecords,
    bestRecord: filteredRecords[0] ?? null,
  };
};

export const filterShieldTokenRecords = (
  records: ShieldRecord[],
  amount: bigint,
  tokenId: bigint,
): ShieldDecryptedRecordsResult => {
  const filteredRecords = records.filter((record) =>
    isBridgeTokenRecordMatch(record, amount, tokenId),
  );
  return {
    walletType: BRIDGE_ALEO_WALLET.SHIELD,
    filteredRecords,
    bestRecord: filteredRecords[0] ?? null,
  };
};

export const emptyBridgeDecryptedRecordsResult = (): UnknownDecryptedRecordsResult => ({
  walletType: null,
  filteredRecords: [],
  bestRecord: null,
});

// ---------------------------------------------------------------------------
// New unified helpers (Phase 4.3 additions)
// ---------------------------------------------------------------------------

/**
 * Filter unspent records matching the given tokenId.
 * Works for both Puzzle and Shield wallet record types.
 */
export const filterUnspentRecords = (
  records: BridgeAleoRecord[],
  tokenId: bigint,
): BridgeAleoRecord[] => {
  return records.filter((record) => {
    if (!isBridgeRecordUnspent(record)) return false;
    const recordTokenId = getBridgeRecordTokenId(record);
    return recordTokenId !== null && recordTokenId === tokenId;
  });
};

/**
 * Find the record with the smallest amount that still satisfies the targetAmount.
 * Prefers the tightest fit to minimize leftover credits from splitting.
 * Returns null when no matching record exists.
 */
export const findClosestRecord = (
  records: BridgeAleoRecord[],
  targetAmount: bigint,
): BridgeAleoRecord | null => {
  const eligible = records.filter(
    (r) => isBridgeRecordUnspent(r) && getBridgeRecordAmount(r) >= targetAmount,
  );
  if (eligible.length === 0) return null;

  return eligible.reduce((best, current) => {
    const bestAmount = getBridgeRecordAmount(best);
    const currentAmount = getBridgeRecordAmount(current);
    return currentAmount < bestAmount ? current : best;
  });
};
